/**
 * Canvas 2D 渲染器。
 *
 * 绘制层次：
 *   1. 背景层 — camera / 图片 / 视频
 *   2. 皮影贴图层 — 按骨骼锚点仿射变换贴图
 *   3. 骨骼层 — 调试连线（可选视觉）
 */
import * as params from './params';
import {
  composeMirrorAffine,
  computeAffineFromTwoPoints,
  imageIndexToKeypointIndex,
} from './piying';
import {buildKeypointsWithVirtual, CUSTOM_SKELETON_PAIRS, getTorsoCenterVideo} from './pose_util';

/** 多人追踪模式下，不同 poseId 对应的骨架颜色 */
const COLOR_PALETTE = [
  '#ffffff', '#800000', '#469990', '#e6194b', '#42d4f4', '#fabed4', '#aaffc3',
  '#9a6324', '#000075', '#f58231', '#4363d8', '#ffd8b1', '#dcbeff', '#808000',
  '#ffe119', '#911eb4', '#bfef45', '#f032e6', '#3cb44b', '#a9a9a9'
];

/** 获取图片或视频的实际像素尺寸 */
function getMediaSize(source) {
  if (source instanceof HTMLVideoElement) {
    return {width: source.videoWidth, height: source.videoHeight};
  }
  if (source instanceof HTMLImageElement) {
    return {width: source.naturalWidth, height: source.naturalHeight};
  }
  return {width: 0, height: 0};
}

/**
 * 计算 object-fit: cover 的绘制区域。
 * 保持宽高比铺满画布，超出部分居中裁剪。
 */
function computeCoverLayout(sourceWidth, sourceHeight, canvasWidth, canvasHeight) {
  const sourceAspect = sourceWidth / sourceHeight;
  const canvasAspect = canvasWidth / canvasHeight;

  if (sourceAspect > canvasAspect) {
    const drawHeight = canvasHeight;
    const drawWidth = sourceWidth * (canvasHeight / sourceHeight);
    return {
      drawWidth,
      drawHeight,
      offsetX: (canvasWidth - drawWidth) / 2,
      offsetY: 0,
    };
  }

  const drawWidth = canvasWidth;
  const drawHeight = sourceHeight * (canvasWidth / sourceWidth);
  return {
    drawWidth,
    drawHeight,
    offsetX: 0,
    offsetY: (canvasHeight - drawHeight) / 2,
  };
}

export class RendererCanvas2d {
  constructor(canvas, piyingManager = null) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.piyingManager = piyingManager;
    this.canvasWidth = 0;
    this.canvasHeight = 0;
    this.poseSourceWidth = 0;
    this.poseSourceHeight = 0;
  }

  /**
   * 同步画布尺寸与骨骼坐标映射。
   * poseSource 尺寸来自摄像头，用于关键点坐标换算。
   */
  resize(poseSourceWidth, poseSourceHeight) {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.canvasWidth = this.canvas.width;
    this.canvasHeight = this.canvas.height;
    this.poseSourceWidth = poseSourceWidth;
    this.poseSourceHeight = poseSourceHeight;
    this.updatePoseCoverLayout();
  }

  /** 根据摄像头分辨率更新骨骼 cover 布局与缩放比例 */
  updatePoseCoverLayout() {
    const layout = computeCoverLayout(
        this.poseSourceWidth, this.poseSourceHeight,
        this.canvasWidth, this.canvasHeight);
    this.drawWidth = layout.drawWidth;
    this.drawHeight = layout.drawHeight;
    this.offsetX = layout.offsetX;
    this.offsetY = layout.offsetY;
    this.scaleX = this.drawWidth / this.poseSourceWidth;
    this.scaleY = this.drawHeight / this.poseSourceHeight;
  }

  /** 将摄像头坐标系下的关键点映射到画布坐标系 */
  mapKeypoint(keypoint) {
    return {
      x: this.offsetX + keypoint.x * this.scaleX,
      y: this.offsetY + keypoint.y * this.scaleY,
      score: keypoint.score,
    };
  }

  /**
   * 主绘制入口。
   * rendererParams: [背景源, 是否镜像背景, 姿态数组, 模型是否正在切换, 空闲提示]
   */
  draw(rendererParams) {
    const [backgroundSource, mirrorBackground, poses, isModelChanged,
           idleMessage] = rendererParams;
    this.drawBackground(backgroundSource, mirrorBackground);

    if (poses && poses.length > 0 && !isModelChanged) {
      // 皮影与骨骼层单独做水平镜像，与自拍预览方向一致
      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.translate(this.canvasWidth, 0);
      this.ctx.scale(-1, 1);
      this.drawResults(poses);
      this.ctx.restore();
    }

    if (idleMessage) {
      this.drawIdleOverlay(idleMessage);
    }
  }

  /** 未运行时居中提示 */
  drawIdleOverlay(message) {
    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.ctx.fillStyle = '#fff';
    this.ctx.font = '20px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(
        message, this.canvasWidth / 2, this.canvasHeight / 2);
    this.ctx.restore();
  }

  /**
   * 绘制背景层。
   * camera 模式下 mirror=true，与摄像头预览方向一致；
   * 图片/视频背景不镜像。
   */
  drawBackground(source, mirror) {
    if (source == null) {
      return;
    }

    const {width, height} = getMediaSize(source);
    if (width === 0 || height === 0) {
      return;
    }

    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);

    const layout = computeCoverLayout(
        width, height, this.canvasWidth, this.canvasHeight);

    if (mirror) {
      this.ctx.translate(this.canvasWidth, 0);
      this.ctx.scale(-1, 1);
    }

    this.ctx.drawImage(
        source,
        layout.offsetX,
        layout.offsetY,
        layout.drawWidth,
        layout.drawHeight);
    this.ctx.restore();
  }

  drawResults(poses) {
    for (let i = 0; i < poses.length; i++) {
      this.drawResult(poses[i], i);
    }
  }

  drawResult(pose, personIndex = 0) {
    if (pose.keypoints == null) {
      return;
    }
    this.drawPiying(pose.keypoints, personIndex);
    if (params.STATE.display.showSkeleton) {
      this.drawSkeleton(pose.keypoints, pose.id);
    }
    if (params.STATE.display.showBoundingBox) {
      this.drawBoundingBox(pose.keypoints, pose.id);
    }
    if (params.STATE.display.showTrackId) {
      this.drawTrackId(pose);
    }
  }

  /** 在躯干中心显示追踪 ID（需抵消外层水平镜像，保证文字正向可读） */
  drawTrackId(pose) {
    if (!params.STATE.modelConfig.enableTracking || pose.id == null) {
      return;
    }
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;
    const center = getTorsoCenterVideo(pose.keypoints, scoreThreshold);
    if (center == null) {
      return;
    }
    const mapped = this.mapKeypoint(center);
    const color = this.getPoseOverlayColor(pose.id);
    const label = `ID: ${pose.id}`;

    this.ctx.save();
    // 外层为 scale(-1,1)，在锚点处再镜像一次，使文字正向显示
    this.ctx.translate(mapped.x, 0);
    this.ctx.scale(-1, 1);
    this.ctx.translate(-mapped.x, 0);

    this.ctx.font = 'bold 18px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'bottom';
    const metrics = this.ctx.measureText(label);
    const padX = 6;
    const padY = 4;
    const boxW = metrics.width + padX * 2;
    const boxH = 22 + padY * 2;
    const boxX = mapped.x - boxW / 2;
    const boxY = mapped.y - boxH - 8;

    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    this.ctx.fillRect(boxX, boxY, boxW, boxH);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(boxX, boxY, boxW, boxH);
    this.ctx.fillStyle = color;
    this.ctx.fillText(label, mapped.x, boxY + boxH - padY - 2);
    this.ctx.restore();
  }

  /**
   * 按骨骼锚点将皮影贴图仿射变换后绘制到 canvas。
   * 按帧内人物序号循环取套装；贴图按文件名顺序号叠加。
   */
  drawPiying(keypoints, personIndex = 0) {
    if (this.piyingManager == null || !this.piyingManager.isReady()) {
      return;
    }

    const items = this.piyingManager.getItemsForPerson(personIndex);
    if (items.length === 0) {
      return;
    }

    const extended = buildKeypointsWithVirtual(keypoints);
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

    for (const item of items) {
      const [anchorA, anchorB] = item.anchors;
      const kpIndexA = imageIndexToKeypointIndex(anchorA.imageIndex);
      const kpIndexB = imageIndexToKeypointIndex(anchorB.imageIndex);

      if (kpIndexA == null || kpIndexB == null) {
        continue;
      }

      const rawA = extended[kpIndexA];
      const rawB = extended[kpIndexB];
      if (rawA == null || rawB == null) {
        continue;
      }

      const dstA = this.mapKeypoint(rawA);
      const dstB = this.mapKeypoint(rawB);
      const scoreA = dstA.score != null ? dstA.score : 1;
      const scoreB = dstB.score != null ? dstB.score : 1;
      if (scoreA < scoreThreshold || scoreB < scoreThreshold) {
        continue;
      }

      const srcA = {x: anchorA.x, y: anchorA.y};
      const srcB = {x: anchorB.x, y: anchorB.y};
      const matrix = computeAffineFromTwoPoints(srcA, srcB, dstA, dstB);
      if (matrix == null) {
        continue;
      }

      // setTransform 会覆盖外层镜像，需将镜像合并进仿射矩阵
      const mirrored = composeMirrorAffine(matrix, this.canvasWidth);

      this.ctx.save();
      this.ctx.setTransform(
          mirrored.a, mirrored.b, mirrored.c, mirrored.d,
          mirrored.e, mirrored.f);
      this.ctx.drawImage(item.image, 0, 0);
      this.ctx.restore();
    }
  }

  /** 骨骼线/边界框颜色：追踪开启且区分颜色时按 id 分色 */
  getPoseOverlayColor(poseId) {
    const usePersonColor = params.STATE.modelConfig.enableTracking &&
        params.STATE.display.colorByPerson &&
        poseId != null;
    return usePersonColor ? COLOR_PALETTE[poseId % 20] : 'White';
  }

  /**
   * 绘制自定义骨骼连线（调试层）。
   */
  drawSkeleton(keypoints, poseId) {
    const color = this.getPoseOverlayColor(poseId);
    this.ctx.fillStyle = color;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    const extended = buildKeypointsWithVirtual(keypoints);
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

    CUSTOM_SKELETON_PAIRS.forEach(([i, j]) => {
      const raw1 = extended[i];
      const raw2 = extended[j];
      if (raw1 == null || raw2 == null) {
        return;
      }

      const kp1 = this.mapKeypoint(raw1);
      const kp2 = this.mapKeypoint(raw2);
      const score1 = kp1.score != null ? kp1.score : 1;
      const score2 = kp2.score != null ? kp2.score : 1;

      if (score1 >= scoreThreshold && score2 >= scoreThreshold) {
        this.ctx.beginPath();
        this.ctx.moveTo(kp1.x, kp1.y);
        this.ctx.lineTo(kp2.x, kp2.y);
        this.ctx.stroke();
      }
    });
  }

  /**
   * 绘制包围骨骼关键点的边界框。
   * 仅统计超过 scoreThreshold 的关键点（含虚拟点 17、18）。
   */
  drawBoundingBox(keypoints, poseId) {
    const extended = buildKeypointsWithVirtual(keypoints);
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasPoint = false;

    for (const kp of extended) {
      if (kp == null) {
        continue;
      }
      const score = kp.score != null ? kp.score : 1;
      if (score < scoreThreshold) {
        continue;
      }
      const mapped = this.mapKeypoint(kp);
      minX = Math.min(minX, mapped.x);
      minY = Math.min(minY, mapped.y);
      maxX = Math.max(maxX, mapped.x);
      maxY = Math.max(maxY, mapped.y);
      hasPoint = true;
    }

    if (!hasPoint) {
      return;
    }

    const pad = params.BBOX_PADDING;
    minX -= pad;
    minY -= pad;
    maxX += pad;
    maxY += pad;

    const color = this.getPoseOverlayColor(poseId);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;
    this.ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
  }
}
